import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  Loader2 
} from 'lucide-react';
import { 
  parseBotMappingCsv, 
  validateMappingRow, 
  importBotMappings, 
  generateCsvTemplate,
  type BotMappingCsvRow,
  type ParsedMappingRow,
  type ImportResult 
} from '@/services/bot-mapping-csv-service';

interface BotMappingCsvImportProps {
  onImportComplete?: () => void;
}

export const BotMappingCsvImport: React.FC<BotMappingCsvImportProps> = ({ onImportComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [csvRows, setCsvRows] = useState<BotMappingCsvRow[]>([]);
  const [validatedRows, setValidatedRows] = useState<ParsedMappingRow[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [validationProgress, setValidationProgress] = useState(0);

  const handleDownloadTemplate = () => {
    const csvContent = generateCsvTemplate();
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bot-mapping-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded successfully');
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setCsvRows([]);
    setValidatedRows([]);
    setImportResult(null);

    try {
      const content = await selectedFile.text();
      const rows = parseBotMappingCsv(content);
      setCsvRows(rows);
      toast.success(`Parsed ${rows.length} rows from CSV`);
    } catch (error: any) {
      toast.error(`Error parsing CSV: ${error.message}`);
      setFile(null);
    }
  };

  const handleValidate = async () => {
    if (csvRows.length === 0) return;

    setIsValidating(true);
    setValidationProgress(0);
    const validated: ParsedMappingRow[] = [];

    try {
      for (let i = 0; i < csvRows.length; i++) {
        const row = csvRows[i];
        const parsedRow = await validateMappingRow(row, i);
        validated.push(parsedRow);
        setValidationProgress(((i + 1) / csvRows.length) * 100);
      }

      setValidatedRows(validated);
      const validCount = validated.filter(r => r.errors.length === 0).length;
      const errorCount = validated.filter(r => r.errors.length > 0).length;
      
      toast.success(`Validation complete: ${validCount} valid, ${errorCount} errors`);
    } catch (error: any) {
      toast.error(`Validation failed: ${error.message}`);
    } finally {
      setIsValidating(false);
      setValidationProgress(0);
    }
  };

  const handleImport = async () => {
    if (validatedRows.length === 0) return;

    const validRows = validatedRows.filter(r => r.errors.length === 0);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setIsImporting(true);
    
    try {
      const result = await importBotMappings(validRows, skipDuplicates);
      setImportResult(result);
      
      if (result.success) {
        toast.success(`Successfully imported ${result.imported} mappings`);
        onImportComplete?.();
      } else {
        toast.warning(`Imported ${result.imported} mappings with ${result.failed} failures`);
      }
    } catch (error: any) {
      toast.error(`Import failed: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const resetState = () => {
    setFile(null);
    setCsvRows([]);
    setValidatedRows([]);
    setImportResult(null);
    setValidationProgress(0);
  };

  const validCount = validatedRows.filter(r => r.errors.length === 0).length;
  const errorCount = validatedRows.filter(r => r.errors.length > 0).length;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" onClick={() => setIsOpen(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Import Bot Mappings from CSV
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Download Template */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Step 1: Download Template</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                Download the CSV template with example data and proper column headers.
              </p>
              <Button variant="outline" onClick={handleDownloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </CardContent>
          </Card>

          {/* Upload File */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Step 2: Upload CSV File</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="csv-file">Select CSV File</Label>
                  <Input
                    id="csv-file"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="mt-1"
                  />
                </div>
                {file && (
                  <div className="text-sm text-muted-foreground">
                    Selected: {file.name} ({csvRows.length} rows)
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Validate */}
          {csvRows.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Step 3: Validate Data</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Validate bot identifiers and data format before importing.
                  </p>
                  {isValidating && (
                    <div className="space-y-2">
                      <Progress value={validationProgress} />
                      <p className="text-sm text-center">Validating rows...</p>
                    </div>
                  )}
                  <Button 
                    onClick={handleValidate} 
                    disabled={isValidating}
                    className="w-full"
                  >
                    {isValidating ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Validate Data
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Validation Results */}
          {validatedRows.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center justify-between">
                  Validation Results
                  <div className="flex gap-2">
                    <Badge variant="default">{validCount} Valid</Badge>
                    {errorCount > 0 && <Badge variant="destructive">{errorCount} Errors</Badge>}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-60 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Row</TableHead>
                        <TableHead>Parent → Child</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Errors</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {validatedRows.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 2}</TableCell>
                          <TableCell className="text-sm">
                            {csvRows[index]?.PARENT_BOT_IDENTIFIER} → {csvRows[index]?.CHILD_BOT_IDENTIFIER}
                          </TableCell>
                          <TableCell>
                            {row.errors.length === 0 ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-red-600">
                            {row.errors.join('; ')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Import Options & Execute */}
          {validCount > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Step 4: Import Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="skip-duplicates"
                      checked={skipDuplicates}
                      onCheckedChange={setSkipDuplicates}
                    />
                    <Label htmlFor="skip-duplicates" className="text-sm">
                      Skip duplicate mappings (recommended)
                    </Label>
                  </div>
                  
                  <Button 
                    onClick={handleImport}
                    disabled={isImporting || validCount === 0}
                    className="w-full"
                  >
                    {isImporting ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Import {validCount} Valid Mappings
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Import Results */}
          {importResult && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Import Complete
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>Successfully imported:</span>
                    <Badge variant="default">{importResult.imported}</Badge>
                  </div>
                  {importResult.failed > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Failed:</span>
                      <Badge variant="destructive">{importResult.failed}</Badge>
                    </div>
                  )}
                  
                  {importResult.errors.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-red-600">Errors:</p>
                      <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                        {importResult.errors.map((error, index) => (
                          <div key={index} className="flex gap-2">
                            <span className="text-muted-foreground">Row {error.row}:</span>
                            <span className="text-red-600">{error.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-3">
                    <Button variant="outline" onClick={resetState} className="flex-1">
                      Import Another File
                    </Button>
                    <Button onClick={() => setIsOpen(false)} className="flex-1">
                      Close
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};